import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getDownloadExtension } from '../utils/downloadFilename';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

export default function VotePage() {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedVote, setSelectedVote] = useState(null);

  useEffect(() => {
    fetchCandidateDetails();
  }, [candidateId]);

  const fetchCandidateDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/voting/candidates', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const foundCandidate = response.data.find(c => c.id === candidateId);
      if (foundCandidate) {
        setCandidate(foundCandidate);
      } else {
        toast.error('Candidato non trovato');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Errore nel caricamento del candidato');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCV = async () => {
    try {
      const response = await axios.get(`/members/${candidate.id}/cv`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        responseType: 'blob'
      });
      const fileExtension = getDownloadExtension({
        filePath: candidate.cv_path,
        contentDisposition: response.headers?.['content-disposition']
      });
      
      // Crea un link per il download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CV_${candidate.first_name}_${candidate.last_name}${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore durante il download del CV');
      console.error('Download CV error:', error);
    }
  };

  const handleDownloadMotivationLetter = async () => {
    try {
      const response = await axios.get(`/members/${candidate.id}/motivation-letter`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        responseType: 'blob'
      });
      const fileExtension = getDownloadExtension({
        filePath: candidate.motivation_letter_path,
        contentDisposition: response.headers?.['content-disposition']
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Lettera_Motivazionale_${candidate.first_name}_${candidate.last_name}${fileExtension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore durante il download della lettera motivazionale');
      console.error('Download motivation letter error:', error);
    }
  };

  const handleVoteSubmit = async () => {
    if (!selectedVote) {
      toast.error('Seleziona SÌ o NO');
      return;
    }

    if (!feedback.trim()) {
      toast.error('Il feedback è obbligatorio');
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(
        '/voting/vote',
        {
          candidate_id: candidateId,
          vote: selectedVote,
          feedback: feedback
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      toast.success('Voto registrato con successo!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la votazione');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-20 p-6 max-w-7xl mx-auto">
          <p className="text-center text-gray-500">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return null;
  }

  const hasCv = Boolean(String(candidate.cv_path || '').trim());
  const hasMotivationField = Object.prototype.hasOwnProperty.call(candidate, 'motivation_letter_path');
  const hasMotivationLetter = hasMotivationField
    ? Boolean(String(candidate.motivation_letter_path || '').trim())
    : true;
  const showDocumentsCard = hasCv || hasMotivationLetter;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-20 p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Vota candidato</h1>
          <p className="text-gray-600">Esprimi il tuo voto e lascia un feedback sul candidato</p>
        </div>

        <div className="max-w-4xl">{/* Contenuto */}
          {/* Candidate Header Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-2xl">
                {candidate.first_name} {candidate.last_name}
              </CardTitle>
              <CardDescription>{candidate.email}</CardDescription>
            </CardHeader>
          </Card>

          {/* Informazioni personali */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Informazioni personali</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Nome</p>
                  <p className="text-gray-900">{candidate.first_name}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-500 mb-1">Cognome</p>
                  <p className="text-gray-900">{candidate.last_name}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="text-gray-900">{candidate.email}</p>
                </div>
                
                {candidate.phone && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Telefono</p>
                    <p className="text-gray-900">{candidate.phone_prefix || '+39'} {candidate.phone}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Località */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Località</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {candidate.country && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Paese</p>
                    <p className="text-gray-900">{candidate.country}</p>
                  </div>
                )}

                {candidate.province && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Provincia</p>
                    <p className="text-gray-900">{candidate.province}</p>
                  </div>
                )}

                {candidate.city && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Città</p>
                    <p className="text-gray-900">{candidate.city}</p>
                  </div>
                )}

                {candidate.postal_code && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">CAP</p>
                    <p className="text-gray-900">{candidate.postal_code}</p>
                  </div>
                )}

                {candidate.address && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500 mb-1">Indirizzo</p>
                    <p className="text-gray-900">{candidate.address}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Informazioni Professionali */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Informazioni professionali</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {candidate.company_name && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Nome Azienda</p>
                    <p className="text-gray-900">{candidate.company_name}</p>
                  </div>
                )}

                {candidate.job_category && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Ruolo Aziendale</p>
                    <p className="text-gray-900">{candidate.job_category}</p>
                  </div>
                )}

                {candidate.professional_category && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Categoria professionale</p>
                    <p className="text-gray-900">{candidate.professional_category}</p>
                  </div>
                )}

                {candidate.business_sector && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Settore azienda</p>
                    <p className="text-gray-900">{candidate.business_sector}</p>
                  </div>
                )}

                {candidate.job_title && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Ruolo</p>
                    <p className="text-gray-900">{candidate.job_title}</p>
                  </div>
                )}

                {candidate.vat_number && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Partita IVA</p>
                    <p className="text-gray-900">{candidate.vat_number}</p>
                  </div>
                )}

                {candidate.tax_code && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Codice Fiscale</p>
                    <p className="text-gray-900">{candidate.tax_code}</p>
                  </div>
                )}

                {candidate.linkedin_url && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500 mb-1">LinkedIn URL</p>
                    <a 
                      href={candidate.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {candidate.linkedin_url}
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Documenti candidatura */}
          {showDocumentsCard && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Documenti candidatura</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-3">
                {hasCv && (
                  <Button
                    onClick={handleDownloadCV}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Scarica CV
                  </Button>
                )}
                {hasMotivationLetter && (
                  <Button
                    onClick={handleDownloadMotivationLetter}
                    className="bg-blue-600 hover:bg-indigo-700"
                  >
                    Scarica lettera motivazionale
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Informazioni Aggiuntive */}
          {candidate.additional_info && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Informazioni Aggiuntive</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-900 whitespace-pre-wrap">{candidate.additional_info}</p>
              </CardContent>
            </Card>
          )}

          {/* Vote Section */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Il tuo voto</CardTitle>
              <CardDescription>Seleziona il tuo voto e lascia un feedback</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Vote Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <Button
                  size="lg"
                  onClick={() => setSelectedVote('yes')}
                  className={`py-8 text-lg font-semibold ${
                    selectedVote === 'yes'
                      ? 'bg-green-600 hover:bg-green-700 ring-4 ring-green-200'
                      : 'bg-green-500 hover:bg-green-600'
                  }`}
                >
                  👍 SÌ
                </Button>
                <Button
                  size="lg"
                  onClick={() => setSelectedVote('no')}
                  className={`py-8 text-lg font-semibold ${
                    selectedVote === 'no'
                      ? 'bg-red-600 hover:bg-red-700 ring-4 ring-red-200'
                      : 'bg-red-500 hover:bg-red-600'
                  }`}
                >
                  👎 NO
                </Button>
              </div>

              {/* Feedback */}
              <div>
                <Label htmlFor="feedback" className="text-base font-semibold mb-2 block">
                  Feedback (obbligatorio)
                </Label>
                <Textarea
                  id="feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Scrivi il tuo feedback sul candidato..."
                  rows={6}
                />
                <p className="text-sm text-gray-500 mt-2">
                  Il tuo feedback sarà visibile al Consiglio Direttivo
                </p>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleVoteSubmit}
                  disabled={submitting || !selectedVote || !feedback.trim()}
                  className="flex-1 bg-black hover:bg-gray-800 text-white py-6"
                >
                  {submitting ? 'Invio in corso...' : 'Conferma Voto'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
